
/* ============================== src/components/views/TasksList.jsx ============================== */
// path: frontend/src/components/views/TasksList.jsx
/**
 * Тело карточки задачи в списке. Унифицировано с TaskCard (kcard-зоны),
 * отличие — статус-чип и .kcard--list.
 *
 * RBAC в контекстном меню — capabilities через useAuth() (меню в
 * menus/taskMenu.js). user остаётся только для «Открыть» и
 * employee-info (EmployeeCardLink).
 */
import { useMemo, memo } from "react";
import { TASK_STATUSES, PRIORITIES } from "../../utils/constants";
import { fmtDMY, TODAY, daysDiff, humanizeDeadline } from "../../utils/date";
import { ColorDot, ColoredSpan } from "../ColoredSpan";
import FloatingMenu from "../FloatingMenu";
import { PriorityStrip } from "../PriorityStrip";
import { buildTaskMenu } from "../menus";
import { useAuth } from "../../hooks/useAuth";
import { chatKey } from "../../utils/chatKey";
import { UnreadBadge } from "../UnreadBadge";
import { Clickable } from "../Clickable";
import EmployeeCardLink from "../EmployeeCardLink";
import { TagsList } from "../TagsList";
import { FactPlan } from "../FactPlan";
import { useEventCallback } from "../../hooks/useEventCallback";
import { getProjectColor } from "../../utils/projectHelpers";

const TaskCardRow = memo(function TaskCardRow({
  task,
  project,
  assignee,
  unread,
  factHours,
  priorityDef,
  projectColor,
  overdue,
  soon,
  status,
  statusColor,
  buildMenu,
  openTask,
  openTag,
}) {
  const menuItems = useMemo(() => buildMenu(task), [buildMenu, task]);

  const hasTags = Array.isArray(task.tags) && task.tags.length > 0;

  const deadlineLabel = task.deadline ? humanizeDeadline(task.deadline) : null;
  const deadlineClass = overdue ? "kdl late" : soon ? "kdl soon" : "kdl";

  return (
    <FloatingMenu items={menuItems}>
      {({ anchorProps }) => (
        <Clickable
          as="div"
          {...anchorProps}
          className="kcard kcard--list"
          onClick={() => openTask(task.id)}
        >
          <PriorityStrip color={priorityDef.color} />

          <div className="kcard-zone">
            <div className="kcard-title-row">
              <div className="kcard-title">{task.title}</div>
              <ColoredSpan
                color={statusColor}
                className="st-chip st-chip--tinted kcard-status"
              >
                {status}
              </ColoredSpan>
            </div>
            {hasTags && (
              <TagsList
                tags={task.tags}
                onTagClick={openTag}
                interactive
                maxVisible={2}
              />
            )}
          </div>

          <div className="kcard-zone">
            <div className="kcard-proj">
              <ColorDot color={projectColor} className="pdot" />
              {project?.code || "Без проекта"}
            </div>
            <div className="kcard-meta">
              {assignee ? (
                <div className="kcard-manager">
                  <span className="kcard-manager-avatar">
                    <EmployeeCardLink employee={assignee} size="xs" />
                  </span>
                  <span className="kcard-manager-name">
                    {assignee.last} {assignee.first}
                  </span>
                </div>
              ) : (
                <span />
              )}
              <FactPlan
                fact={factHours}
                plan={task.plannedHours}
                color={priorityDef.color}
              />
            </div>
          </div>

          <div className="kcard-zone">
            <div className="kcard-foot">
              {deadlineLabel && (
                <span className={deadlineClass} title={fmtDMY(task.deadline)}>
                  {deadlineLabel}
                </span>
              )}
              <div className="kcard-foot-end">
                <ColoredSpan color={priorityDef.color} className="prio-chip">
                  {priorityDef.label}
                </ColoredSpan>
                <UnreadBadge
                  count={unread}
                  onClick={() => openTask(task.id, "chat")}
                  variant="neutral"
                />
              </div>
            </div>
          </div>
        </Clickable>
      )}
    </FloatingMenu>
  );
});

export default function TasksList({
  tasks,
  db,
  user,
  unreadIndex,
  layout = "tiles",
  openTask,
  openTag,
  onMove,
  onDelete,
  onCopy,
  onMakeTemplate,
}) {
  const { capabilities } = useAuth();

  const buildMenu = useEventCallback((task) =>
    buildTaskMenu({
      task,
      user,
      capabilities,
      db,
      openTask,
      onMove,
      onDelete,
      onCopy,
      onMakeTemplate,
    }),
  );

  if (!tasks.length) {
    return (
      <div className="empty-note tasks-empty">
        Нет задач, соответствующих фильтрам
      </div>
    );
  }

  const gridClassName =
    layout === "rows"
      ? "pj-grid pj-grid--rows pj-grid--tasks"
      : "pj-grid pj-grid--tasks";

  return (
    <div className={gridClassName}>
      {tasks.map((task) => {
        const project = db.projects.find((p) => p.id === task.projectId);
        const assignee = task.assigneeId
          ? db.employees.find((e) => e.id === task.assigneeId)
          : null;

        const factHours = task.logs.reduce((sum, log) => sum + log.hours, 0);

        const isFinal = task.status === "closed" || task.status === "cancelled";
        const overdue = task.deadline && !isFinal && task.deadline < TODAY;
        const soon =
          task.deadline &&
          !overdue &&
          !isFinal &&
          daysDiff(TODAY, task.deadline) <= 3;

        const statusDef = TASK_STATUSES[task.status];
        const status = statusDef?.label || task.status;
        const statusColor = statusDef?.color || "#64748b";

        const priorityDef = PRIORITIES[task.priority] || {
          label: task.priority || "Нет",
          color: "#64748b",
        };
        const projectColor = getProjectColor(project);
        const unread = unreadIndex.get(chatKey({ taskId: task.id })) || 0;

        return (
          <TaskCardRow
            key={task.id}
            task={task}
            project={project}
            assignee={assignee}
            unread={unread}
            factHours={factHours}
            priorityDef={priorityDef}
            projectColor={projectColor}
            overdue={overdue}
            soon={soon}
            status={status}
            statusColor={statusColor}
            buildMenu={buildMenu}
            openTask={openTask}
            openTag={openTag}
          />
        );
      })}
    </div>
  );
}
